import { useMemo } from "react";

import { SteppedMultiColumnDropdown, SelectField } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";

import { createPathAwareDropdown, column } from "./DefinitionBuilder";

import type { JiraGrouping_tagKeys$key } from "./__generated__/JiraGrouping_tagKeys.graphql";

/**
 * Converts grouping dropdown selections to groupBy array format
 */
export function convertGroupingSelections(
  selections: string[],
  tagItems: { id: string; name: string }[],
): string[] {
  const [groupingType, selectedTagId] = selections;
  let groupBy: string[] = [];

  // Map grouping types to their GraphQL groupBy array
  switch (groupingType) {
    case "account-resource-tag-policy": {
      // If no tag is selected, return empty array (incomplete selection)
      if (!selectedTagId) {
        return [];
      }
      // Find the tag name from the ID
      const tagName =
        tagItems.find((item) => item.id === selectedTagId)?.name ||
        selectedTagId;
      groupBy = ["account", `resource_tag:${tagName}`, "policy"];
      break;
    }
    case "policy-resource-tag": {
      // If no tag is selected, return empty array (incomplete selection)
      if (!selectedTagId) {
        return [];
      }
      // Find the tag name from the ID
      const tagName =
        tagItems.find((item) => item.id === selectedTagId)?.name ||
        selectedTagId;
      groupBy = ["policy", `resource_tag:${tagName}`];
      break;
    }
    case "policy-account":
      groupBy = ["policy", "account"];
      break;
    case "policy-fewest-issues":
      groupBy = ["policy"];
      break;
  }

  return groupBy;
}

/**
 * Extracts tag name from groupBy array and returns display name
 */
function getTagDisplayName(
  groupBy: string[],
  tagItems: { id: string; name: string }[],
): string {
  const tagItem = groupBy.find((item) => item.startsWith("resource_tag:"));
  if (!tagItem) return "";
  const tagName = tagItem.replace("resource_tag:", "");
  return tagItems.find((item) => item.name === tagName)?.name || tagName;
}

/**
 * Converts groupBy array back to display string
 */
export function convertGroupByToDisplayString(
  groupBy: string[] | null | undefined,
  tagItems: { id: string; name: string }[],
): string {
  if (!groupBy || groupBy.length === 0) {
    return "Select Grouping";
  }

  const hasAccount = groupBy.includes("account");
  const hasPolicy = groupBy.includes("policy");
  const hasResourceTag = groupBy.some((item) =>
    item.startsWith("resource_tag:"),
  );

  // Account, Resource Tag and Policy (most actionable)
  if (hasAccount && hasPolicy && hasResourceTag) {
    const tagDisplayName = getTagDisplayName(groupBy, tagItems);
    return `Account, Resource Tag and Policy (most actionable) - ${tagDisplayName}`;
  }

  // Policy and Resource Tag
  if (hasPolicy && hasResourceTag && !hasAccount) {
    const tagDisplayName = getTagDisplayName(groupBy, tagItems);
    return `Policy and Resource Tag - ${tagDisplayName}`;
  }

  // Policy and Account
  if (hasPolicy && hasAccount) {
    return "Policy and Account";
  }

  // Policy (fewest issues)
  if (groupBy.length === 1 && groupBy[0] === "policy") {
    return "Policy (fewest issues)";
  }

  // Fallback: return a generic display
  return groupBy.join(", ");
}

/** Props for JiraGrouping component */
interface Props {
  /** Relay fragment reference for resource tag keys */
  tagKeysRef: JiraGrouping_tagKeys$key;
  /** Currently selected groupBy array (if any) */
  selectedGroupBy?: string[] | null;
  /** Callback when selection changes, receives converted groupBy array */
  onChange?: (groupBy: string[]) => void;
}

export function JiraGrouping({
  tagKeysRef,
  selectedGroupBy,
  onChange: onChangeProp,
}: Props) {
  const { data } = usePaginationFragment(
    graphql`
      fragment JiraGrouping_tagKeys on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "JiraGroupingTagKeysPaginationQuery") {
        jiraGroupingTagKeys: resourceTagKeys(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "JiraGrouping__jiraGroupingTagKeys") {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    tagKeysRef,
  );

  // Transform tag keys data for the dropdown
  const tagItems = useMemo(
    () =>
      data.jiraGroupingTagKeys?.edges.map((edge) => ({
        id: edge.node.key,
        name: edge.node.key,
      })) ?? [],
    [data.jiraGroupingTagKeys?.edges],
  );

  // Configuration
  const config = useMemo(
    () =>
      createPathAwareDropdown()
        .criteria([
          {
            id: "policy-fewest-issues",
            name: "Policy (fewest issues)",
            element: (
              <div>
                <div>Policy (fewest issues)</div>
                <div className="text-label-extra-small text-legacy-navy-L40">
                  Example: Tag EC2 Resources
                </div>
              </div>
            ),
          },
          {
            id: "account-resource-tag-policy",
            name: "Account, Resource Tag and Policy (most actionable)",
            element: (
              <div>
                <div>Account, Resource Tag and Policy (most actionable)</div>
                <div className="text-label-extra-small text-legacy-navy-L40">
                  Example: [Account-1] [App=Example] Tag EC2 Resources
                </div>
              </div>
            ),
          },
          {
            id: "policy-resource-tag",
            name: "Policy and Resource Tag",
            element: (
              <div>
                <div>Policy and Resource Tag</div>
                <div className="text-label-extra-small text-legacy-navy-L40">
                  Example: [App=Example] Tag EC2 Resources
                </div>
              </div>
            ),
          },
          {
            id: "policy-account",
            name: "Policy and Account",
            element: (
              <div>
                <div>Policy and Account</div>
                <div className="text-label-extra-small text-legacy-navy-L40">
                  Example: [Account-1] Tag EC2 Resources
                </div>
              </div>
            ),
          },
        ])
        // Paths that need resource tag selection
        .path("account-resource-tag-policy", (path) =>
          path.column(
            column
              .search("Select Resource Tag", tagItems)
              .withPlaceholder("Search or enter value")
              .allowCustomValue(),
          ),
        )
        .path("policy-resource-tag", (path) =>
          path.column(
            column
              .search("Select Resource Tag", tagItems)
              .withPlaceholder("Search or enter value")
              .allowCustomValue(),
          ),
        )
        // Empty paths for other options
        .path("policy-account", (path) => path)
        .path("policy-fewest-issues", (path) => path)
        .build(),
    [tagItems],
  );

  const onChange = (selections: string[]) => {
    const groupBy = convertGroupingSelections(selections, tagItems);
    onChangeProp?.(groupBy);
  };

  // Generate display text from selected groupBy value
  const displayText = useMemo(
    () => convertGroupByToDisplayString(selectedGroupBy, tagItems),
    [selectedGroupBy, tagItems],
  );

  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <SelectField className="w-[692px]" isOpen={isOpen} value={displayText} />
  );

  return (
    <SteppedMultiColumnDropdown
      config={config}
      onChange={onChange}
      trigger={trigger}
    />
  );
}
